import { type ReactElement } from 'react';

import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import {
  formatDays,
  inviteWaitDays,
  STATE_LABELS,
  type AddressEntry,
} from './connection-actions';

const ACTIONABLE_STATES: AddressEntry['state'][] = [
  'accept',
  'connect',
  'invite',
  'inviteAgain',
];

const stateText = ({ account, state }: AddressEntry): string => {
  const waitDays = account && inviteWaitDays(account);
  return state === 'invited' && waitDays
    ? `${STATE_LABELS.invited} · ${formatDays(waitDays)}`
    : STATE_LABELS[state];
};

/** The addresses of a person, one row each with the state of the address, which open from `children`. A click on a row
 * does what the state of its address calls for. */
export const AddressChooser = ({
  entries,
  onChoose,
  children,
}: {
  entries: AddressEntry[];
  onChoose: (entry: AddressEntry) => void;
  children: ReactElement;
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
    {/* The list fits its addresses, so that addresses that begin alike can be told apart, and cuts the longer ones */}
    <DropdownMenu.Content
      align="end"
      fit
      className="min-w-[20rem] max-w-[30rem]"
    >
      {entries.map((entry) => {
        const isWaiting = entry.state === 'invited';
        return (
          <DropdownMenu.Item
            key={entry.email}
            // A waiting invite stays reachable by keyboard, so that its wait can be read
            aria-disabled={isWaiting || undefined}
            className={cn(
              'justify-between gap-4',
              isWaiting && 'cursor-default opacity-50'
            )}
            onSelect={(event) => {
              if (isWaiting) event.preventDefault();
              else onChoose(entry);
            }}
          >
            {/* A long address is cut, but its state is what the user acts on, so the state is not */}
            <span className="min-w-0 truncate">{entry.email}</span>
            <span
              className={cn(
                'shrink-0 whitespace-nowrap text-xs',
                ACTIONABLE_STATES.includes(entry.state)
                  ? 'text-foreground'
                  : 'text-foreground-subtle'
              )}
            >
              {stateText(entry)}
            </span>
          </DropdownMenu.Item>
        );
      })}
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);
